import type { ILocalPreferences, ITableFiltersOnly } from "../interfaces/models/IUserPreferences";

const STORAGE_KEY_PREFIX = "wsg_local_prefs";

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}`;
}

export function clearLocalPreferences(userId: string | undefined): void {
  if (!userId || typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch (error: unknown) {
    console.error("Error clearing local preferences:", error);
  }
}

export function getLocalPreferences(userId: string | undefined): ILocalPreferences {
  if (!userId || typeof window === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as ILocalPreferences;
    return parsed ?? {};
  } catch {
    return {};
  }
}

export function setLocalPreferences(
  userId: string | undefined,
  update: Partial<ILocalPreferences> | ((prev: ILocalPreferences) => ILocalPreferences)
): void {
  if (!userId || typeof window === "undefined") {
    return;
  }
  try {
    const prev = getLocalPreferences(userId);
    const next = typeof update === "function" ? update(prev) : { ...prev, ...update };
    localStorage.setItem(getStorageKey(userId), JSON.stringify(next));
  } catch (error: unknown) {
    console.error("Error setting local preferences:", error);
  }
}

export function getTableFilters(userId: string | undefined, tableKey: string): ITableFiltersOnly {
  const prefs = getLocalPreferences(userId);
  return prefs?.tableFilters?.[tableKey] ?? {};
}

export function setTableFilters(
  userId: string | undefined,
  tableKey: string,
  partial: ITableFiltersOnly
): void {
  if (!userId || typeof window === "undefined") {
    return;
  }
  const safePartial: ITableFiltersOnly = {};
  if (partial.filters !== undefined) {
    safePartial.filters = partial.filters;
  }
  if (partial.inputFilters !== undefined) {
    safePartial.inputFilters = partial.inputFilters;
  }
  setLocalPreferences(userId, (prev) => ({
    ...prev,
    tableFilters: {
      ...(prev.tableFilters ?? {}),
      [tableKey]: { ...(prev.tableFilters?.[tableKey] ?? {}), ...safePartial },
    },
  }));
}
